// Système de gestion des permissions par rôle
// Rôles : agent_terrain, agence, soebra

export type Role = 'agent_terrain' | 'agence' | 'soebra' | 'admin';

export type Permission = 
  // Rapports d'Activité
  | 'reports:create'
  | 'reports:submit'
  | 'reports:view_all'
  | 'reports:edit'
  | 'reports:delete'
  | 'reports:pre_validate'
  | 'reports:approve'
  | 'reports:reject'
  | 'reports:add_recommendation'
  
  // Gestion de Projets
  | 'projects:view_assigned'
  | 'projects:view_all'
  | 'projects:create'
  | 'projects:edit'
  | 'projects:delete'
  | 'projects:manage'
  | 'projects:validate_milestones'
  | 'projects:archive'
  
  // Gestion des Sondages
  | 'surveys:respond'
  | 'surveys:create'
  | 'surveys:delete'
  | 'surveys:deploy'
  | 'surveys:view_analytics'
  | 'surveys:export'
  | 'surveys:view_readonly'
  
  // Chat Interne
  | 'chat:participate'
  | 'chat:create_rooms'
  | 'chat:manage_rooms'
  
  // Inventaire/Ressources
  | 'inventory:scan'
  | 'inventory:declare'
  | 'inventory:manage'
  | 'inventory:view_all'
  | 'inventory:view_strategic'
  
  // Base de Connaissances
  | 'knowledge:view_essential'
  | 'knowledge:upload'
  | 'knowledge:manage'
  | 'knowledge:approve'
  
  // Suivi des KPIs
  | 'kpis:view_own'
  | 'kpis:define_operational'
  | 'kpis:view_all'
  | 'kpis:define_strategic';

// Matrice des permissions par rôle
const rolePermissions: Record<Role, Permission[]> = {
  agent_terrain: [
    // Rapports
    'reports:create',
    'reports:submit',
    
    // Projets
    'projects:view_assigned',
    
    // Sondages
    'surveys:respond',
    
    // Chat
    'chat:participate',
    
    // Inventaire
    'inventory:scan',
    'inventory:declare',
    
    // Base de connaissances
    'knowledge:view_essential',
    
    // KPIs
    'kpis:view_own',
  ],
  
  agence: [
    // Toutes les permissions pour l'agence (même que l'admin)
    'reports:create',
    'reports:submit',
    'reports:view_all',
    'reports:edit',
    'reports:delete',
    'reports:pre_validate',
    'reports:approve',
    'reports:reject',
    'reports:add_recommendation',
    'projects:view_assigned',
    'projects:view_all',
    'projects:create',
    'projects:edit',
    'projects:delete',
    'projects:manage',
    'projects:validate_milestones',
    'projects:archive',
    'surveys:respond',
    'surveys:create',
    'surveys:delete',
    'surveys:deploy',
    'surveys:view_analytics',
    'surveys:export',
    'surveys:view_readonly',
    'chat:participate',
    'chat:create_rooms',
    'chat:manage_rooms',
    'inventory:scan',
    'inventory:declare',
    'inventory:manage',
    'inventory:view_all',
    'inventory:view_strategic',
    'knowledge:view_essential',
    'knowledge:upload',
    'knowledge:manage',
    'knowledge:approve',
    'kpis:view_own',
    'kpis:define_operational',
    'kpis:view_all',
    'kpis:define_strategic',
  ],
  
  soebra: [
    // Rapports
    'reports:view_all',
    'reports:approve',
    'reports:reject',
    'reports:add_recommendation',
    
    // Projets
    'projects:view_all',
    'projects:validate_milestones',
    
    // Sondages
    'surveys:view_readonly',
    'surveys:view_analytics',
    'surveys:export',
    
    // Chat
    'chat:participate',
    'chat:create_rooms',
    
    // Inventaire
    'inventory:view_strategic',
    
    // Base de connaissances
    'knowledge:approve',
    
    // KPIs
    'kpis:view_all',
    'kpis:define_strategic',
  ],
  
  admin: [
    // Toutes les permissions pour l'admin
    'reports:create',
    'reports:submit',
    'reports:view_all',
    'reports:edit',
    'reports:delete',
    'reports:pre_validate',
    'reports:approve',
    'reports:reject',
    'reports:add_recommendation',
    'projects:view_assigned',
    'projects:view_all',
    'projects:create',
    'projects:edit',
    'projects:delete',
    'projects:manage',
    'projects:validate_milestones',
    'projects:archive',
    'surveys:respond',
    'surveys:create',
    'surveys:delete',
    'surveys:deploy',
    'surveys:view_analytics',
    'surveys:export',
    'surveys:view_readonly',
    'chat:participate',
    'chat:create_rooms',
    'chat:manage_rooms',
    'inventory:scan',
    'inventory:declare',
    'inventory:manage',
    'inventory:view_all',
    'inventory:view_strategic',
    'knowledge:view_essential',
    'knowledge:upload',
    'knowledge:manage',
    'knowledge:approve',
    'kpis:view_own',
    'kpis:define_operational',
    'kpis:view_all',
    'kpis:define_strategic',
  ],
};

/**
 * Vérifie si un rôle a une permission spécifique
 */
export const hasPermission = (role: Role, permission: Permission): boolean => {
  return rolePermissions[role]?.includes(permission) || false;
};

/**
 * Vérifie si un rôle a au moins une des permissions spécifiées
 */
export const hasAnyPermission = (role: Role, permissions: Permission[]): boolean => {
  return permissions.some(permission => hasPermission(role, permission));
};

/**
 * Vérifie si un rôle a toutes les permissions spécifiées
 */
export const hasAllPermissions = (role: Role, permissions: Permission[]): boolean => {
  return permissions.every(permission => hasPermission(role, permission));
};

/**
 * Obtient toutes les permissions d'un rôle
 */
export const getRolePermissions = (role: Role): Permission[] => {
  return rolePermissions[role] || [];
};

/**
 * Vérifie si un utilisateur peut créer un projet
 */
export const canCreateProject = (role: Role): boolean => {
  return hasPermission(role, 'projects:create');
};

/**
 * Vérifie si un utilisateur peut modifier un projet
 */
export const canEditProject = (role: Role): boolean => {
  return hasPermission(role, 'projects:edit') || hasPermission(role, 'projects:manage');
};

/**
 * Vérifie si un utilisateur peut voir tous les projets
 */
export const canViewAllProjects = (role: Role): boolean => {
  return hasPermission(role, 'projects:view_all');
};

/**
 * Vérifie si un utilisateur peut créer un rapport
 */
export const canCreateReport = (role: Role): boolean => {
  return hasPermission(role, 'reports:create');
};

/**
 * Vérifie si un utilisateur peut pré-valider un rapport (Agence)
 */
export const canPreValidateReport = (role: Role): boolean => {
  return hasPermission(role, 'reports:pre_validate');
};

/**
 * Vérifie si un utilisateur peut approuver un rapport (SOBEBRA)
 */
export const canApproveReport = (role: Role): boolean => {
  return hasPermission(role, 'reports:approve');
};

/**
 * Vérifie si un utilisateur peut créer un formulaire/sondage
 */
export const canCreateSurvey = (role: Role): boolean => {
  return hasPermission(role, 'surveys:create');
};

/**
 * Vérifie si un utilisateur peut répondre à un sondage
 */
export const canRespondToSurvey = (role: Role): boolean => {
  return hasPermission(role, 'surveys:respond');
};

/**
 * Vérifie si un utilisateur peut voir les analytics
 */
export const canViewAnalytics = (role: Role): boolean => {
  return hasPermission(role, 'surveys:view_analytics') || hasPermission(role, 'surveys:view_readonly');
};

